import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PlanillonService } from '../services';
import { ConceptoService } from '../../conceptos/services';
import type { PlanillaDetalleUpdate, PlanillonRow, DetalleConcepto } from '../types/planillon';
import type { ConceptoPlanilla } from '../../conceptos/types/concepto';
import { usePlanillonStore } from '../store';

interface Props {
  open: boolean;
  onClose: () => void;
  row: PlanillonRow | null;
}

const TIPO_LABELS: Record<string, string> = { R: 'Ingreso', D: 'Descuento', A: 'Aporte' };
const TIPO_COLORS: Record<string, 'success' | 'error' | 'info'> = { R: 'success', D: 'error', A: 'info' };

const EditDetalleDialog: React.FC<Props> = ({ open, onClose, row }) => {
  const { selectedPlanilla, fetchPlanillon } = usePlanillonStore();

  const [formData, setFormData] = useState({
    diasLaborados: 30,
    diasNoLaborados: 0,
    diasSubsidiados: 0,
    horasNormales: 240,
    horasExtras25: 0,
    horasExtras35: 0,
    horasExtras100: 0,
    horasExtraNocturna: 0,
    minutosTardanza: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Conceptos manuales
  const [conceptosDetalle, setConceptosDetalle] = useState<DetalleConcepto[]>([]);
  const [conceptosDisponibles, setConceptosDisponibles] = useState<ConceptoPlanilla[]>([]);
  const [selectedConcepto, setSelectedConcepto] = useState<ConceptoPlanilla | null>(null);
  const [montoNuevo, setMontoNuevo] = useState<string>('');

  useEffect(() => {
    if (row && open) {
      setFormData({
        diasLaborados: row.diasLaborados,
        diasNoLaborados: row.diasNoLaborados,
        diasSubsidiados: row.diasSubsidiados,
        horasNormales: row.horasNormales,
        horasExtras25: row.horasExtras25,
        horasExtras35: row.horasExtras35,
        horasExtras100: row.horasExtras100,
        horasExtraNocturna: row.horasExtraNocturna,
        minutosTardanza: row.minutosTardanza,
      });
      loadConceptos();
    }
  }, [row, open]);

  const loadConceptos = async () => {
    if (!row) return;
    try {
      const [detalle, disponibles] = await Promise.all([
        PlanillonService.getConceptosDetalle(row.idPlanillaDetalle),
        ConceptoService.getAll(),
      ]);
      setConceptosDetalle(detalle);
      setConceptosDisponibles(disponibles.filter((c: ConceptoPlanilla) => c.activo));
    } catch {
      // silently fail
    }
  };

  const handleChange = (field: string, value: string) => {
    const numValue = Number(value) || 0;
    setFormData((prev) => ({ ...prev, [field]: numValue }));
    setError(null);
  };

  const handleSave = async () => {
    if (!row || !selectedPlanilla) return;

    if (formData.diasLaborados < 0 || formData.diasLaborados > 31) {
      setError('Días laborados debe estar entre 0 y 31');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const detalle: PlanillaDetalleUpdate = {
        idPlanillaDetalle: row.idPlanillaDetalle,
        idPlanilla: selectedPlanilla.idPlanilla,
        idTrabajador: row.idTrabajador,
        ...formData,
      };
      await PlanillonService.updateDetalle(row.idPlanillaDetalle, detalle);
      fetchPlanillon(selectedPlanilla.idPlanilla);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAgregarConcepto = async () => {
    if (!row || !selectedConcepto || !montoNuevo) return;
    const monto = parseFloat(montoNuevo);
    if (isNaN(monto) || monto <= 0) {
      setError('Ingrese un monto válido mayor a 0');
      return;
    }
    try {
      await PlanillonService.agregarConceptoManual(row.idPlanillaDetalle, selectedConcepto.idConcepto, monto);
      setSelectedConcepto(null);
      setMontoNuevo('');
      setError(null);
      await loadConceptos();
      if (selectedPlanilla) fetchPlanillon(selectedPlanilla.idPlanilla);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al agregar concepto');
    }
  };

  const handleEliminarConcepto = async (idDetalleConcepto: number) => {
    try {
      await PlanillonService.eliminarConceptoManual(idDetalleConcepto);
      await loadConceptos();
      if (selectedPlanilla) fetchPlanillon(selectedPlanilla.idPlanilla);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar concepto');
    }
  };

  if (!row) return null;

  const conceptosManuales = conceptosDetalle.filter(c => !c.esCalculado);
  const conceptosCalculadosList = conceptosDetalle.filter(c => c.esCalculado);
  const esEditable = selectedPlanilla?.estado === 'BORRADOR' || selectedPlanilla?.estado === 'CALCULADA';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Detalle - {row.nombreTrabajador}</DialogTitle>
      <DialogContent>
        {/* ── Días y Horas ── */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Días
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField
            label="Laborados"
            type="number"
            size="small"
            value={formData.diasLaborados}
            onChange={(e) => handleChange('diasLaborados', e.target.value)}
            inputProps={{ min: 0, max: 31 }}
          />
          <TextField
            label="No Laborados"
            type="number"
            size="small"
            value={formData.diasNoLaborados}
            onChange={(e) => handleChange('diasNoLaborados', e.target.value)}
            inputProps={{ min: 0, max: 31 }}
          />
          <TextField
            label="Subsidiados"
            type="number"
            size="small"
            value={formData.diasSubsidiados}
            onChange={(e) => handleChange('diasSubsidiados', e.target.value)}
            inputProps={{ min: 0, max: 31 }}
          />
        </Box>

        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Horas
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField
            label="Normales"
            type="number"
            size="small"
            value={formData.horasNormales}
            onChange={(e) => handleChange('horasNormales', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="HE 25%"
            type="number"
            size="small"
            value={formData.horasExtras25}
            onChange={(e) => handleChange('horasExtras25', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="HE 35%"
            type="number"
            size="small"
            value={formData.horasExtras35}
            onChange={(e) => handleChange('horasExtras35', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="HE 100%"
            type="number"
            size="small"
            value={formData.horasExtras100}
            onChange={(e) => handleChange('horasExtras100', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="HE Nocturna"
            type="number"
            size="small"
            value={formData.horasExtraNocturna}
            onChange={(e) => handleChange('horasExtraNocturna', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="Min. Tardanza"
            type="number"
            size="small"
            value={formData.minutosTardanza}
            onChange={(e) => handleChange('minutosTardanza', e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Conceptos calculados (solo lectura) ── */}
        {conceptosCalculadosList.length > 0 && (
          <>
            <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              Conceptos Calculados (sistema)
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conceptosCalculadosList.map((c) => (
                  <TableRow key={c.idPlanillaDetalleConcepto}>
                    <TableCell>{c.concepto?.codigo}</TableCell>
                    <TableCell>{c.concepto?.descripcion}</TableCell>
                    <TableCell>
                      <Chip
                        label={TIPO_LABELS[c.concepto?.tipoConcepto || ''] || c.concepto?.tipoConcepto}
                        color={TIPO_COLORS[c.concepto?.tipoConcepto || ''] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{c.monto.toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {c.detalleCalculo}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Conceptos manuales (editables) ── */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Conceptos Manuales (variables de este periodo)
        </Typography>

        {conceptosManuales.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conceptosManuales.map((c) => (
                <TableRow key={c.idPlanillaDetalleConcepto}>
                  <TableCell>{c.concepto?.codigo}</TableCell>
                  <TableCell>{c.concepto?.descripcion}</TableCell>
                  <TableCell>
                    <Chip
                      label={TIPO_LABELS[c.concepto?.tipoConcepto || ''] || c.concepto?.tipoConcepto}
                      color={TIPO_COLORS[c.concepto?.tipoConcepto || ''] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{c.monto.toFixed(2)}</TableCell>
                  <TableCell>
                    {esEditable && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleEliminarConcepto(c.idPlanillaDetalleConcepto)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {conceptosManuales.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No hay conceptos manuales agregados
          </Typography>
        )}

        {/* ── Agregar concepto manual ── */}
        {esEditable && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              size="small"
              sx={{ flex: 1 }}
              options={conceptosDisponibles}
              getOptionLabel={(opt) => `${opt.codigo} - ${opt.descripcion}`}
              groupBy={(opt) => TIPO_LABELS[opt.tipoConcepto] || opt.tipoConcepto}
              value={selectedConcepto}
              onChange={(_, val) => setSelectedConcepto(val)}
              renderInput={(params) => <TextField {...params} label="Concepto" />}
            />
            <TextField
              size="small"
              label="Monto"
              type="number"
              sx={{ width: 140 }}
              value={montoNuevo}
              onChange={(e) => setMontoNuevo(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAgregarConcepto}
              disabled={!selectedConcepto || !montoNuevo}
              sx={{ minWidth: 100 }}
            >
              Agregar
            </Button>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar Días/Horas'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDetalleDialog;
